/**
 * Main-thread singleton that manages the Whisper audio analysis Web Worker.
 *
 * Handles:
 * - Loading the Whisper small.en model (~249MB q8, cached in browser)
 * - Decoding audio from URLs (blob: or https:) via AudioContext
 * - Resampling to 16kHz mono for Whisper
 * - Sending samples to the worker for transcription with DTW word timestamps
 * - Returning AnalyzedAudio results compatible with the server-side format
 */

// ── Types ────────────────────────────────────────────────────────────

export interface AnalyzedAudio {
  meta: {duration: number; sampleRate: number; filename: string};
  transcript: {
    text: string;
    words: {word: string; start: number; end: number}[];
    sentences: {
      text: string;
      start: number;
      end: number;
      wordStartIndex: number;
      wordEndIndex: number;
    }[];
  };
  pauses: {start: number; end: number; duration: number; type: string}[];
}

export type AnalysisModelStatus = 'idle' | 'loading' | 'ready' | 'error';

interface PendingRequest {
  resolve: (result: AnalyzedAudio) => void;
  reject: (err: Error) => void;
}

type StatusListener = (status: AnalysisModelStatus, detail?: string) => void;
type ProgressListener = (loaded: number, total: number, file: string) => void;

// ── Client ───────────────────────────────────────────────────────────

class AudioAnalysisClient {
  private worker: Worker | null = null;
  private status: AnalysisModelStatus = 'idle';
  private pending = new Map<string, PendingRequest>();
  private nextId = 0;
  private statusListeners = new Set<StatusListener>();
  private progressListeners = new Set<ProgressListener>();
  private readyPromise: Promise<void> | null = null;
  private readyResolve: (() => void) | null = null;

  /** Start loading the Whisper model. Safe to call multiple times. */
  preload(): void {
    if (this.worker) return;

    this.status = 'loading';
    this.notifyStatus('loading');

    this.readyPromise = new Promise<void>(resolve => {
      this.readyResolve = resolve;
    });

    this.worker = new Worker(
      new URL('./audio-analysis-worker.ts', import.meta.url),
      {type: 'module'},
    );

    this.worker.onmessage = (e: MessageEvent) => {
      const msg = e.data;

      switch (msg.type) {
        case 'status':
          this.status = 'loading';
          this.notifyStatus('loading');
          break;

        case 'progress':
          this.notifyProgress(msg.loaded, msg.total, msg.file);
          break;

        case 'ready':
          this.status = 'ready';
          this.notifyStatus('ready');
          this.readyResolve?.();
          break;

        case 'analyzed': {
          const req = this.pending.get(msg.id);
          if (req) {
            this.pending.delete(msg.id);
            req.resolve(msg.analysis);
          }
          break;
        }

        case 'error': {
          if (msg.id) {
            const req = this.pending.get(msg.id);
            if (req) {
              this.pending.delete(msg.id);
              req.reject(new Error(msg.error));
            }
          } else {
            this.status = 'error';
            this.notifyStatus('error', msg.error);
          }
          break;
        }
      }
    };

    this.worker.onerror = (err: ErrorEvent) => {
      console.error('[AudioAnalysis] Worker error:', err.message);
      this.status = 'error';
      this.notifyStatus('error', err.message);
    };
  }

  /** Wait until the Whisper model is loaded. */
  async waitReady(): Promise<void> {
    if (this.status === 'ready') return;
    if (!this.readyPromise) this.preload();
    return this.readyPromise!;
  }

  /**
   * Analyze audio from a URL (blob: or https:).
   * Decodes the audio on the main thread, resamples to 16kHz,
   * and sends to the worker for Whisper transcription with DTW
   * cross-attention word-level timestamps.
   *
   * When `transcript` is provided (e.g. after TTS generation), the known
   * text is sent to the worker to bias Whisper and produce more accurate
   * word boundaries -- mirroring the dev-mode forced-alignment behavior.
   */
  async analyze(audioUrl: string, transcript?: string): Promise<AnalyzedAudio> {
    await this.waitReady();

    // Fetch the audio data
    const response = await fetch(audioUrl);
    const arrayBuffer = await response.arrayBuffer();

    // Decode to PCM using AudioContext
    const audioCtx = new AudioContext({sampleRate: 16000});
    let audioBuffer: AudioBuffer;
    try {
      audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    } finally {
      audioCtx.close();
    }

    // Get mono channel, already at 16kHz from our AudioContext
    const samples = audioBuffer.getChannelData(0);

    // Extract filename from URL
    let filename = 'audio';
    try {
      const url = new URL(audioUrl);
      const path = url.pathname;
      filename = path.split('/').pop() || 'audio';
    } catch {
      // blob URLs etc
    }

    const id = String(++this.nextId);
    return new Promise<AnalyzedAudio>((resolve, reject) => {
      this.pending.set(id, {resolve, reject});
      // Transfer the buffer to avoid copying
      const samplesBuffer = samples.buffer.slice(
        samples.byteOffset,
        samples.byteOffset + samples.byteLength,
      );
      const transferred = new Float32Array(samplesBuffer);
      this.worker!.postMessage(
        {
          type: 'analyze',
          id,
          samples: transferred,
          sampleRate: 16000,
          filename,
          transcript: transcript?.trim() || undefined,
        },
        [transferred.buffer],
      );
    });
  }

  getStatus(): AnalysisModelStatus {
    return this.status;
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  onProgress(listener: ProgressListener): () => void {
    this.progressListeners.add(listener);
    return () => this.progressListeners.delete(listener);
  }

  destroy(): void {
    this.worker?.terminate();
    this.worker = null;
    this.status = 'idle';
    this.readyPromise = null;
    this.readyResolve = null;
    for (const req of this.pending.values()) {
      req.reject(new Error('Worker terminated'));
    }
    this.pending.clear();
  }

  private notifyStatus(status: AnalysisModelStatus, detail?: string) {
    for (const fn of this.statusListeners) fn(status, detail);
  }

  private notifyProgress(loaded: number, total: number, file: string) {
    for (const fn of this.progressListeners) fn(loaded, total, file);
  }
}

/** Global singleton. */
export const audioAnalysisClient = new AudioAnalysisClient();
